<template>
  <div class="wrp">
    <div class="settings-block">
      <div class="settings-block__title">
        <div class="settings-block__title-text">
          {{ t('Referral Program') }}
        </div>
      </div>
      <div class="mb-10 w80">
        {{
          t(
            'Involve users and receive a regular income from all their payments. Use the links below to attract your referrals. All registered users will be permanently added to your referrals. To all visitors, who came from your link, we will set the cookie for 30 days - registration in that period will be counted as your referral.',
          )
        }}
      </div>

      <div class="mb-4">
        <div class="balance-title">{{ t('Your Referral Percent') }}</div>
        <div class="balance-value">{{ store.user.referralPercent }}%</div>
      </div>

      <div class="invited-users__copy-input-wrapper">
        <div class="flex-grow-1">
          <ui-copy-input
            :value="`https://bemob.com/?ref=${store.user.referralId}`"
            :label="t('Homepage URL')"
          />
        </div>
        <div class="flex-grow-1">
          <ui-copy-input
            :value="`https://bemob.com/?ref=${store.user.referralId}&reg`"
            :label="t('Register URL')"
          />
        </div>
      </div>
    </div>

    <div class="settings-block">
      <div class="settings-block__title">
        <div class="settings-block__title-text">
          {{ t('Invited Users') }}
        </div>
      </div>
      <ui-table
        class="invited-users__table"
        :loading="referredLoading"
        :headers="headers"
        :items="rows"
        :fetch-page="updatePagination"
        :total-items="totalRows"
        :use-search="false"
        :virtualize="false"
      >
        <template #append-row>
          <div class="table-row invited-users__append-row">
            <div class="table-cell">
              {{ t('Total invited') }}: {{ referredSummary.totalInvited }}
            </div>
            <div class="table-cell"></div>
            <div class="table-cell"></div>
            <div class="table-cell">{{ last30DaysCommissionFormatted }}</div>
            <div class="table-cell">{{ totalCommissionFormatted }}</div>
          </div>
        </template>
      </ui-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import moment from 'moment-timezone';
import numeral from 'numeral';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import { getReferredUsersApiRequest } from '@/services/api/referral-program-api';
import type { User } from '@/services/models';

const store = useStore();
const { t } = useI18n();

const referredLoading = ref(false);
const referredSummary = ref({
  totalInvited: 0,
  last30DaysCommission: undefined as number | undefined,
  totalCommission: undefined as number | undefined,
});
const rows = ref<unknown[]>([]);
const totalRows = ref(0);

const last30DaysCommissionFormatted = computed(() =>
  numeral((referredSummary.value.last30DaysCommission ?? 0) / 100).format('$ 0,0.00'),
);

const totalCommissionFormatted = computed(() =>
  numeral((referredSummary.value.totalCommission ?? 0) / 100).format('$ 0,0.00'),
);

const headers = computed(() => [
  { title: t('Email'), value: 'email', sortable: true },
  { title: t('Registered at'), value: 'created', sortable: true },
  { title: t('Plan'), value: 'subscriptionPlan', sortable: true },
  { title: t('Last 30 Days Commission'), value: 'last30DaysCommission', sortable: true },
  { title: t('Total Commission'), value: 'totalCommission', sortable: true },
]);

const updatePagination = async (data: {
  itemsPerPage: number;
  page: number;
  sortBy: { key: string; order: string }[];
}) => {
  const { itemsPerPage, page, sortBy } = data;

  referredLoading.value = true;
  try {
    const payload = await getReferredUsersApiRequest({
      offset: (page - 1) * itemsPerPage,
      limit: itemsPerPage,
      orderDir: sortBy[0]?.order,
      orderBy: sortBy[0]?.key,
    });
    const timezone = (store.user as User).defaultTimezone;

    rows.value = payload.items!.map((row: unknown) => {
      const item = row as {
        email: string;
        created: string;
        subscriptionPlan: string;
        last30DaysCommission: string;
        totalCommission: string;
      };
      return {
        email: item.email,
        created: item.created
          ? moment.utc(item.created).tz(timezone).format('DD MMM YYYY hh:mm')
          : '',
        subscriptionPlan: item.subscriptionPlan,
        last30DaysCommission: item.last30DaysCommission || '$ 0.00',
        totalCommission: item.totalCommission || '$ 0.00',
      };
    });
    totalRows.value = payload.count!;
    referredSummary.value = {
      totalInvited: payload.count!,
      last30DaysCommission: payload.last30DaysCommission,
      totalCommission: payload.totalCommission,
    };
  } finally {
    referredLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.wrp {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.balance-title {
  font-weight: bold;
  color: var(--color-graphite);
}
.balance-value {
  font-size: 24px;
  color: var(--color-blue);
  padding: 16px 0;
}
.invited-users__copy-input-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.invited-users__table {
  width: 100%;
  min-width: 0;

  :deep(.ui-table__inner) {
    min-width: 600px;
  }

  :deep(.ui-table__inner .table-header > .table-cell),
  :deep(.ui-table__inner .table-body .table-cell),
  :deep(.ui-table__inner .ui-table__append-footer .table-cell) {
    text-align: left;
  }

  :deep(.ui-table__inner .table-header .table-cell:not(:first-child) .table-cell__inner) {
    justify-content: flex-start;
  }

  :deep(.table-header .table-cell:nth-child(1)),
  :deep(.table-body .table-row .table-cell:nth-child(1)),
  :deep(.invited-users__append-row .table-cell:nth-child(1)) {
    width: 28%;
    min-width: 0;
  }

  :deep(.table-header .table-cell:nth-child(2)),
  :deep(.table-body .table-row .table-cell:nth-child(2)),
  :deep(.invited-users__append-row .table-cell:nth-child(2)) {
    width: 22%;
    min-width: 0;
  }

  :deep(.table-header .table-cell:nth-child(3)),
  :deep(.table-body .table-row .table-cell:nth-child(3)),
  :deep(.invited-users__append-row .table-cell:nth-child(3)) {
    width: 14%;
    min-width: 0;
  }

  :deep(.table-header .table-cell:nth-child(4)),
  :deep(.table-header .table-cell:nth-child(5)),
  :deep(.table-body .table-row .table-cell:nth-child(4)),
  :deep(.table-body .table-row .table-cell:nth-child(5)),
  :deep(.invited-users__append-row .table-cell:nth-child(4)),
  :deep(.invited-users__append-row .table-cell:nth-child(5)) {
    white-space: normal;
    min-width: 0;
  }

  :deep(.invited-users__append-row .table-cell) {
    font-size: 13px;
    color: var(--color-graphite);
    border-top: 1px solid var(--color-gray10);
    padding-top: 12px;
    padding-bottom: 8px;
  }

  :deep(.table-body .table-cell .sort-icon) {
    margin: 0 6px;
  }
}
</style>
